import { Injectable } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { RoutingConfigService, RoutingService } from '@spartacus/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'
import { CheckoutConfig } from '../config'
import { MbCheckoutStep } from '../models'

@Injectable({
  providedIn: 'root'
})
export class MbCheckoutStepService {

  allSteps: MbCheckoutStep[] | undefined = []

  readonly steps$: BehaviorSubject<MbCheckoutStep[]> = new BehaviorSubject<MbCheckoutStep[]>([])

  readonly activeStepIndex$: Observable<number | undefined> = this.routingService
  .getRouterState()
  .pipe(
    switchMap((router) => {
      const activeStepUrl = router.state.context.id
      return this.steps$.pipe(
        map((steps) => {
          let activeIndex: number = 0
          steps.forEach((step, index) => {
            const routeUrl = `/${
              this.routingConfigService?.getRouteConfig(step?.routeName)?.paths?.[0]
            }`
            if (routeUrl === activeStepUrl) {
              activeIndex = index
            }
          })
          return activeIndex
        })
      )
    })
  )

  constructor (protected routingService: RoutingService,
               protected checkoutConfig: CheckoutConfig,
               protected routingConfigService: RoutingConfigService) {
    this.resetSteps()
  }

  back (activatedRoute: ActivatedRoute): void {
    const previousUrl = this.getPreviousCheckoutStepUrl(activatedRoute)
    this.routingService.go(previousUrl === null ? 'cart' : previousUrl).catch((error: Error) => console.error(error))
  }

  next (activatedRoute: ActivatedRoute): void {
    const nextUrl = this.getNextCheckoutStepUrl(activatedRoute)
    this.routingService.go(nextUrl).catch((error: Error) => console.error(error))
  }

  getNextCheckoutStepUrl (activatedRoute: ActivatedRoute): string | null {
    const stepIndex = this.getCurrentStepIndex(activatedRoute) as number

    if (stepIndex !== null && stepIndex >= 0) {
      let i = 1
      while (this.allSteps &&
      this.allSteps[stepIndex + i] &&
      this.allSteps[stepIndex + i].disabled) {
        i++
      }
      const nextStep = this.allSteps ? this.allSteps[stepIndex + i] : null
      if (nextStep) {
        return this.getStepUrlFromStepRoute(nextStep.routeName)
      }
    }
    return null
  }

  getPreviousCheckoutStepUrl (activatedRoute: ActivatedRoute): string | null {
    const stepIndex = this.getCurrentStepIndex(activatedRoute) as number

    if (stepIndex !== null && stepIndex >= 0) {
      let i = 1
      while (this.allSteps &&
      this.allSteps[stepIndex - i] &&
      this.allSteps[stepIndex - i].disabled) {
        i++
      }
      const previousStep = this.allSteps ? this.allSteps[stepIndex - i] : null
      if (previousStep) {
        return this.getStepUrlFromStepRoute(previousStep.routeName)
      }
    }
    return null
  }

  getCurrentStepIndex (activatedRoute: ActivatedRoute): number | null | undefined {
    const currentStepUrl = this.getStepUrlFromActivatedRoute(activatedRoute)

    const stepIndex = this.allSteps?.findIndex(
      (step) =>
        currentStepUrl === `/${this.getStepUrlFromStepRoute(step.routeName)}`
    )
    return stepIndex === -1 ? null : stepIndex
  }

  resetSteps (): void {
    this.allSteps = (this.checkoutConfig.checkout?.steps ?? [])
      .filter((step) => !step.disabled && step.isProgressBarStep)
      .map((x) => Object.assign({}, x))
    this.steps$.next(this.allSteps)
  }

  private getStepUrlFromActivatedRoute (
    activatedRoute: ActivatedRoute
  ): string | null {
    return activatedRoute &&
    activatedRoute.snapshot &&
    activatedRoute.snapshot.url
      ? `/${activatedRoute.snapshot.url.join('/')}`
      : null
  }

  private getStepUrlFromStepRoute (stepRoute: string): string | null {
    return (
      this.routingConfigService.getRouteConfig(stepRoute)?.paths?.[0] ?? null
    )
  }
}
